import React, { useState, useEffect } from 'react';
import "../../css/TransportRoutes.css";
import { Search, Plus, MapPin, Clock, User, Bus, Route, Filter, Edit3, Trash2, X, ArrowDown, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../components/Loader';

function TransportRoutes() {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingRoute, setIsAddingRoute] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewRouteDetails, setViewRouteDetails] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    routeId: null,
  });
  const [filters, setFilters] = useState({
    status: "all",
    sortBy: "name",
  });
  const [newRoute, setNewRoute] = useState({
    routeName: "",
    routeNumber: "",
    driver: "",
    busNumber: "",
    startLocation: "",
    endLocation: "",
    departureTime: "",
    estimatedArrival: "",
    stops: [],
  });
  const [newStop, setNewStop] = useState({ name: "", time: "" });
  const [routeStats, setRouteStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/transports", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch transport routes");
      }

      const data = await response.json();
      setRoutes(data.routes);
      setRouteStats(data.stats);
    } catch (error) {
      console.error("Error fetching routes:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addRoute = async (routeData) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/transports/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(routeData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add route");
      }

      const data = await response.json();
      toast.success("Route added successfully!");
      fetchRoutes(); // Refresh the routes list
    } catch (error) {
      console.error("Error adding route:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateRoute = async (id, routeData) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`https://schoolpro-api.uniums.com/api/transports/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(routeData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update route");
      }

      const data = await response.json();
      toast.success("Route updated successfully!");
      fetchRoutes(); // Refresh the routes list
    } catch (error) {
      console.error("Error updating route:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRoute = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`https://schoolpro-api.uniums.com/api/transports/${id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete route");
      }

      toast.success("Route deleted successfully!");
      fetchRoutes(); // Refresh the routes list
      setDeleteConfirmation({
        isOpen: false,
        routeId: null
      });
    } catch (error) {
      console.error("Error deleting route:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Form handlers
  const handleRouteChange = (e) => {
    const { name, value } = e.target;
    setNewRoute({
      ...newRoute,
      [name]: value,
    });
  };

  const handleAddStop = () => {
    if (newStop.name && newStop.time) {
      setNewRoute({
        ...newRoute,
        stops: [...newRoute.stops, { ...newStop }],
      });
      setNewStop({ name: "", time: "" });
    }
  };

  const handleRemoveStop = (index) => {
    const updatedStops = newRoute.stops.filter((_, i) => i !== index);
    setNewRoute({
      ...newRoute,
      stops: updatedStops,
    });
  };

  const handleStopChange = (e) => {
    const { name, value } = e.target;
    setNewStop({
      ...newStop,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedRoute) {
      updateRoute(selectedRoute._id, newRoute);
    } else {
      addRoute(newRoute);
    }
    resetForm();
  };

  const handleToggleRouteStatus = (id) => {
    toggleRouteStatus(id);
  };

  const handleEditRoute = (route) => {
    setSelectedRoute(route);
    setNewRoute({
      ...route,
    });
    setIsAddingRoute(true);
  };

  const handleDeleteConfirmation = (id) => {
    setDeleteConfirmation({
      isOpen: true,
      routeId: id,
    });
  };

  const confirmDelete = () => {
    deleteRoute(deleteConfirmation.routeId);
  };

  const toggleRouteStatusHandler = (id) => {
    toggleRouteStatus(id);
  };

  const cancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      routeId: null,
    });
  };

  useEffect(() => {
    if (searchTerm) {
      const delaySearch = setTimeout(() => {
        searchRoutes(searchTerm, filters.status, filters.sortBy);
      }, 500);

      return () => clearTimeout(delaySearch);
    } else {
      fetchRoutes();
    }
  }, [searchTerm, filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [name]: value };
      searchRoutes(searchTerm, updatedFilters.status, updatedFilters.sortBy);
      return updatedFilters;
    });
  };

  const resetForm = () => {
    setNewRoute({
      routeName: "",
      routeNumber: "",
      driver: "",
      busNumber: "",
      startLocation: "",
      endLocation: "",
      departureTime: "",
      estimatedArrival: "",
      stops: [],
    });
    setSelectedRoute(null);
    setIsAddingRoute(false);
  };

  const toggleRouteStatus = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`https://schoolpro-api.uniums.com/api/transports/${id}/toggle-status`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle route status");
      }

      const data = await response.json();
      toast.success(`Route status changed to ${data.route.status}!`);
      fetchRoutes(); // Refresh the routes list
    } catch (error) {
      console.error("Error toggling route status:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const searchRoutes = async (term = "", status = "all", sortBy = "") => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const queryParams = new URLSearchParams();
      if (term) queryParams.append("term", term);
      if (status !== "all") queryParams.append("status", status);
      if (sortBy) queryParams.append("sortBy", sortBy);

      const response = await fetch(`https://schoolpro-api.uniums.com/api/transports/search?${queryParams.toString()}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to search routes");
      }

      const data = await response.json();
      setRoutes(data);
    } catch (error) {
      console.error("Error searching routes:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openRouteDetails = (route) => {
    setViewRouteDetails(route);
  };

  const closeRouteDetails = () => {
    setViewRouteDetails(null);
  };

  // Filter routes based on search term
  const filteredRoutes = routes.filter(route =>
    route.routeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.routeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.driver.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="transport-container">
      {/* Header Section */}
      <ToastContainer />
      <div className="transport-header">
        <div className="transport-title-container">
          <div className="transport-icon">
            <Route size={28} />
          </div>
          <h1 className="transport-title">Transport Routes Management</h1>
        </div>

        <div className="transport-controls">
          <div className="transport-search-container">
            <Search className="transport-search-icon" size={18} />
            <input
              type="text"
              className="transport-search-input"
              placeholder="Search routes by name, number or driver..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  searchRoutes(searchTerm, filters.status, filters.sortBy);
                }
              }}
            />
          </div>

          <button
            className="transport-filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
          </button>

          <button
            className="transport-add-btn"
            onClick={() => setIsAddingRoute(!isAddingRoute)}
          >
            <Plus size={16} />
            {isAddingRoute ? "Cancel" : "Add New Route"}
          </button>
        </div>

        {showFilters && (
          <div className="transport-filter-options">
            <div className="transport-filter-group">
              <label>Status:</label>
              <select
                className="transport-filter-select"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="transport-filter-group">
              <label>Sort By:</label>
              <select
                className="transport-filter-select"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                <option value="name">Route Name</option>
                <option value="number">Route Number</option>
                <option value="driver">Driver</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="transport-stats">
        <div className="transport-stat-box">
          <span className="transport-stat-number">{routeStats.total}</span>
          <span className="transport-stat-label">Total Routes</span>
        </div>
        <div className="transport-stat-box active">
          <span className="transport-stat-number">{routeStats.active}</span>
          <span className="transport-stat-label">Active Routes</span>
        </div>
        <div className="transport-stat-box inactive">
          <span className="transport-stat-number">{routeStats.inactive}</span>
          <span className="transport-stat-label">Inactive Routes</span>
        </div>
      </div>

      {isLoading && (
        <div className="transport-loader-container">
          <Loader />
        </div>
      )}

      {/* Form Section */}
      {isAddingRoute ? (
        <div className="transport-form-container">
          <div className="transport-form-header">
            <h2>{selectedRoute ? "Edit Route" : "Add New Route"}</h2>
          </div>

          <form onSubmit={handleSubmit} className="transport-form">
            <div className="transport-form-group">
              <label htmlFor="routeName">Route Name</label>
              <input
                type="text"
                id="routeName"
                name="routeName"
                value={newRoute.routeName}
                onChange={handleRouteChange}
                required
              />
            </div>

            <div className="transport-form-row">
              <div className="transport-form-group">
                <label htmlFor="routeNumber">Route Number</label>
                <input
                  type="text"
                  id="routeNumber"
                  name="routeNumber"
                  value={newRoute.routeNumber}
                  onChange={handleRouteChange}
                  required
                />
              </div>

              <div className="transport-form-group">
                <label htmlFor="busNumber">Bus Number</label>
                <input
                  type="text"
                  id="busNumber"
                  name="busNumber"
                  value={newRoute.busNumber}
                  onChange={handleRouteChange}
                  required
                />
              </div>
            </div>

            <div className="transport-form-group">
              <label htmlFor="driver">Driver Name</label>
              <input
                type="text"
                id="driver"
                name="driver"
                value={newRoute.driver}
                onChange={handleRouteChange}
                required
              />
            </div>

            <div className="transport-form-row">
              <div className="transport-form-group">
                <label htmlFor="startLocation">Start Location</label>
                <input
                  type="text"
                  id="startLocation"
                  name="startLocation"
                  value={newRoute.startLocation}
                  onChange={handleRouteChange}
                  required
                />
              </div>

              <div className="transport-form-group">
                <label htmlFor="endLocation">End Location</label>
                <input
                  type="text"
                  id="endLocation"
                  name="endLocation"
                  value={newRoute.endLocation}
                  onChange={handleRouteChange}
                  required
                />
              </div>
            </div>

            <div className="transport-form-row">
              <div className="transport-form-group">
                <label htmlFor="departureTime">Departure Time</label>
                <input
                  type="text"
                  id="departureTime"
                  name="departureTime"
                  placeholder="e.g. 7:00 AM"
                  value={newRoute.departureTime}
                  onChange={handleRouteChange}
                  required
                />
              </div>

              <div className="transport-form-group">
                <label htmlFor="estimatedArrival">Estimated Arrival</label>
                <input
                  type="text"
                  id="estimatedArrival"
                  name="estimatedArrival"
                  placeholder="e.g. 8:00 AM"
                  value={newRoute.estimatedArrival}
                  onChange={handleRouteChange}
                  required
                />
              </div>
            </div>

            <div className="transport-stops-section">
              <h3>Route Stops</h3>

              {newRoute.stops.length > 0 ? (
                <div className="transport-stops-list">
                  {newRoute.stops.map((stop, index) => (
                    <div key={index} className="transport-stop-item">
                      <div className="transport-stop-info">
                        <span className="transport-stop-name">{stop.name}</span>
                        <span className="transport-stop-time">{stop.time}</span>
                      </div>
                      <button
                        type="button"
                        className="transport-remove-stop-btn"
                        onClick={() => handleRemoveStop(index)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="transport-no-stops">
                  <p>No stops added yet. Add stops below.</p>
                </div>
              )}

              <div className="transport-add-stop-form">
                <div className="transport-form-row">
                  <div className="transport-form-group">
                    <input
                      type="text"
                      name="name"
                      placeholder="Stop Name"
                      value={newStop.name}
                      onChange={handleStopChange}
                    />
                  </div>

                  <div className="transport-form-group">
                    <input
                      type="text"
                      name="time"
                      placeholder="Time (e.g. 7:15 AM)"
                      value={newStop.time}
                      onChange={handleStopChange}
                    />
                  </div>

                  <button
                    type="button"
                    className="transport-add-stop-btn"
                    onClick={handleAddStop}
                  >
                    <Plus size={16} /> Add Stop
                  </button>
                </div>
              </div>
            </div>

            <div className="transport-form-actions">
              <button
                type="button"
                className="transport-cancel-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="transport-submit-btn"
              >
                {selectedRoute ? "Update Route" : "Save Route"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          {/* Routes Table */}
          {filteredRoutes.length > 0 ? (
            <div className="transport-table-container">
              <table className="transport-table">
                <thead>
                  <tr>
                    <th>Route Name</th>
                    <th>Route Number</th>
                    <th>Driver</th>
                    <th>Bus Number</th>
                    <th>Start - End</th>
                    <th>Time</th>
                    <th>Stops</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoutes.map((route) => (
                    <tr key={route._id} className={`transport-row-${route.status}`}>
                      <td>
                        <span
                          className="transport-name-cell"
                          onClick={() => openRouteDetails(route)}
                        >
                          {route.routeName}
                        </span>
                      </td>
                      <td>{route.routeNumber}</td>
                      <td>{route.driver}</td>
                      <td>{route.busNumber}</td>
                      <td>
                        <div className="transport-route-points">
                          <div className="transport-location">
                            <MapPin
                              size={14}
                              className="transport-location-icon"
                            />
                            {route.startLocation}
                          </div>
                          <div className="transport-route-arrow">
                            <ArrowDown size={14} />
                          </div>
                          <div className="transport-location">
                            <MapPin
                              size={14}
                              className="transport-location-icon"
                            />
                            {route.endLocation}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="transport-time-info">
                          <div className="transport-time">
                            <Clock size={14} className="transport-time-icon" />
                            Dep: {route.departureTime}
                          </div>
                          <div className="transport-time">
                            <Clock size={14} className="transport-time-icon" />
                            Arr: {route.estimatedArrival}
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          className="transport-stops-badge"
                          onClick={() => openRouteDetails(route)}
                        >
                          {route.stops.length} stops
                        </button>
                      </td>
                      <td>
                        <span
                          className={`transport-status-badge ${route.status}`}
                          onClick={() => toggleRouteStatus(route._id)}
                        >
                          {route.status}
                        </span>
                      </td>
                      <td>
                        <div className="transport-actions">
                          <button
                            className="transport-action-btn transport-edit-btn"
                            onClick={() => handleEditRoute(route)}
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            className="transport-action-btn transport-delete-btn"
                            onClick={() => handleDeleteConfirmation(route._id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="transport-empty">
              <div className="transport-empty-icon">
                <AlertTriangle size={48} />
              </div>
              <h3 className="transport-empty-text">No routes found</h3>
              <p>Try adjusting your search or filters</p>
              {searchTerm && (
                <button
                  className="transport-reset-search"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Route Details Modal */}
      {viewRouteDetails && (
        <div className="transport-modal-overlay">
          <div className="transport-modal transport-details-modal">
            <div className="transport-modal-header">
              <h3>Route Details</h3>
              <button
                className="transport-modal-close"
                onClick={closeRouteDetails}
              >
                <X size={20} />
              </button>
            </div>

            <div className="transport-details-content">
              <div className="transport-details-header">
                <div className="transport-route-info">
                  <h2>{viewRouteDetails.routeName}</h2>
                  <span className="transport-route-number">
                    {viewRouteDetails.routeNumber}
                  </span>
                </div>
                <span
                  className={`transport-status-badge ${viewRouteDetails.status}`}
                >
                  {viewRouteDetails.status}
                </span>
              </div>

              <div className="transport-details-grid">
                <div className="transport-detail-group">
                  <div className="transport-detail-label">
                    <Bus size={16} />
                    Bus Number
                  </div>
                  <div className="transport-detail-value">
                    {viewRouteDetails.busNumber}
                  </div>
                </div>

                <div className="transport-detail-group">
                  <div className="transport-detail-label">
                    <User size={16} />
                    Driver
                  </div>
                  <div className="transport-detail-value">
                    {viewRouteDetails.driver}
                  </div>
                </div>

                <div className="transport-detail-group">
                  <div className="transport-detail-label">
                    <Clock size={16} />
                    Departure Time
                  </div>
                  <div className="transport-detail-value">
                    {viewRouteDetails.departureTime}
                  </div>
                </div>

                <div className="transport-detail-group">
                  <div className="transport-detail-label">
                    <Clock size={16} />
                    Estimated Arrival
                  </div>
                  <div className="transport-detail-value">
                    {viewRouteDetails.estimatedArrival}
                  </div>
                </div>
              </div>

              <div className="transport-route-path">
                <h3>Route Path</h3>
                <div className="transport-route-endpoints">
                  <div className="transport-start-point">
                    <div className="transport-point-marker start"></div>
                    <div className="transport-point-name">
                      {viewRouteDetails.startLocation}
                    </div>
                    <div className="transport-point-time">
                      {viewRouteDetails.departureTime}
                    </div>
                  </div>

                  <div className="transport-route-stops">
                    {viewRouteDetails.stops.map((stop, index) => (
                      <div key={index} className="transport-stop-point">
                        <div className="transport-stop-line"></div>
                        <div className="transport-point-marker stop"></div>
                        <div className="transport-point-details">
                          <div className="transport-point-name">
                            {stop.name}
                          </div>
                          <div className="transport-point-time">
                            {stop.time}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="transport-end-point">
                    <div className="transport-point-marker end"></div>
                    <div className="transport-point-name">
                      {viewRouteDetails.endLocation}
                    </div>
                    <div className="transport-point-time">
                      {viewRouteDetails.estimatedArrival}
                    </div>
                  </div>
                </div>
              </div>

              <div className="transport-modal-actions">
                <button
                  className="transport-cancel-btn"
                  onClick={closeRouteDetails}
                >
                  Close
                </button>
                <button
                  className="transport-edit-route-btn"
                  onClick={() => {
                    handleEditRoute(viewRouteDetails);
                    closeRouteDetails();
                  }}
                >
                  <Edit3 size={16} />
                  Edit Route
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="transport-modal-overlay">
          <div className="transport-modal transport-confirmation-modal">
            <div className="transport-modal-header">
              <h3>Confirm Deletion</h3>
              <button
                className="transport-modal-close"
                onClick={cancelDelete}
              >
                <X size={20} />
              </button>
            </div>

            <div className="transport-confirmation-content">
              <div className="transport-confirmation-icon">
                <AlertTriangle size={48} color="#f44336" />
              </div>
              <h3>Are you sure you want to delete this route?</h3>
              <p>This action cannot be undone. All data associated with this route will be permanently removed.</p>

              <div className="transport-confirmation-actions">
                <button
                  className="transport-cancel-btn"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                <button
                  className="transport-delete-confirm-btn"
                  onClick={confirmDelete}
                >
                  <Trash2 size={16} />
                  Delete Route
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransportRoutes;